import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    FiArrowLeft,
    FiUsers,
    FiSettings,
    FiCopy,
    FiRefreshCw,
    FiAlertCircle,
    FiFileText,
    FiX,
    FiZap,
    FiActivity,
    FiDownload,
    FiFilter
} from "react-icons/fi";
import { Bounce } from "react-toastify";
import { toast } from '../utils/customToast';
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import RemoveMemberModal from "../components/modals/RemoveMemberModal";
import PricingModal from "../components/modals/PricingModal";
import DeleteModal from "../components/modals/DeleteModal";
import PlanStatusBar from "../components/shared/PlanStatusBar";
import TenantStatus from "../components/shared/TenantStatus";
import TenantStatusNotification from "../components/shared/TenantStatusNotification";
import MessageTemplates from "./MessageTemplates";
import { useAuth } from "../context/AuthContext";
import { useApiErrorHandler } from "../hooks/useApiErrorHandler";
import { apiFetch } from "../utils/api";
import { getCurrentPlan, usePlanPermissions } from '../utils/planPermissions';
import usePageTitle from '../hooks/usePageTitle';
import { useTutorial } from '../context/TutorialContext';

// Polices pré-sélectionnées pour la marque
const BRAND_FONTS = [
    { label: 'Inter (défaut)', value: 'Inter' },
    { label: 'Playfair Display', value: 'Playfair Display' },
    { label: 'Merriweather', value: 'Merriweather' },
    { label: 'Montserrat', value: 'Montserrat' },
    { label: 'Raleway', value: 'Raleway' },
    { label: 'Lato', value: 'Lato' },
    { label: 'Poppins', value: 'Poppins' },
    { label: 'Roboto', value: 'Roboto' },
    { label: 'Oswald', value: 'Oswald' },
    { label: 'Nunito', value: 'Nunito' },
    { label: 'DM Serif Display', value: 'DM Serif Display' },
    { label: 'Libre Baskerville', value: 'Libre Baskerville' },
    { label: 'EB Garamond', value: 'EB Garamond' },
    { label: 'Georgia (serif)', value: 'Georgia' },
    { label: 'courier New (mono)', value: 'Courier New' },
];

const TenantSettings = () => {
    usePageTitle('Paramètres');
    const { getAuthHeaders, user, activeTenantId, hasTenantRole } = useAuth();
    const navigate = useNavigate();
    const { notifyAction: tutorialNotify } = useTutorial();

    useEffect(() => {
        tutorialNotify('visited-org-settings');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [tenant, setTenant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tenantName, setTenantName] = useState("");
    const [inviteCode, setInviteCode] = useState("");
    const [members, setMembers] = useState([]);
    const [memberToRemove, setMemberToRemove] = useState(null);
    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [showPricingModal, setShowPricingModal] = useState(false);
    const [showDeleteTenantModal, setShowDeleteTenantModal] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    // Source de vérité unique : l'URL. Supprime le double-state qui causait le clignotement.
    const activeSection = searchParams.get("tab") || "info";

    const handleTabChange = (section) => {
        setSearchParams({ tab: section }, { replace: true });
    };
    const { handleApiError } = useApiErrorHandler();

    // States for message settings (declare hooks at top-level to respect Rules of Hooks)
    const [messageTypes, setMessageTypes] = useState([]); // array of { name, color }
    const [messageRoles, setMessageRoles] = useState([]);
    const [newType, setNewType] = useState('');
    const [newTypeColor, setNewTypeColor] = useState('#3b82f6');
    const [newRole, setNewRole] = useState('');
    const [savingMessagesSettings, setSavingMessagesSettings] = useState(false);

    // Vérifier les permissions du plan actuel (hook appelé avant tout early return)
    const currentPlanName = getCurrentPlan(user, activeTenantId);
    const { canUseTemplates, canUseMessageSettings, canUseCustomBranding } = usePlanPermissions(currentPlanName);

    // States for branding (Enterprise)
    const [brandingName, setBrandingName] = useState('');
    const [brandingFont, setBrandingFont] = useState('Inter');
    const [brandingColor, setBrandingColor] = useState('#111827');
    // taille en rem, entre 0.9 et 1.75 (max pour ne pas agrandir le header)
    // taille en rem, entre 0.9 et 2 (max pour ne pas agrandir le header)
    const [brandingSize, setBrandingSize] = useState(1.25);
    const [savingBranding, setSavingBranding] = useState(false);

    // States for audit log
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditTotal, setAuditTotal] = useState(0);
    const [auditPage, setAuditPage] = useState(1);
    const [auditPages, setAuditPages] = useState(1);
    const [auditAction, setAuditAction] = useState('');
    const [auditLoading, setAuditLoading] = useState(false);

    const fetchAuditLogs = React.useCallback(async (page = 1, action = '') => {
        if (!activeTenantId) return;
        setAuditLoading(true);
        try {
            const params = new URLSearchParams({ page, limit: 50 });
            if (action) params.set('action', action);
            const data = await apiFetch(
                `${process.env.REACT_APP_API_URL}/audit/${activeTenantId}/logs?${params}`,
                { headers: getAuthHeaders() },
                handleApiError
            );
            setAuditLogs(data.logs || []);
            setAuditTotal(data.total || 0);
            setAuditPages(data.pages || 1);
            setAuditPage(page);
        } catch (err) {
            console.error('Error fetching audit logs', err);
        } finally {
            setAuditLoading(false);
        }
    }, [activeTenantId, getAuthHeaders, handleApiError]);

    const handleExportAudit = async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/audit/${activeTenantId}/logs/export`,
                { headers: getAuthHeaders() }
            );
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `audit-${activeTenantId}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error('Erreur lors de l\'export', { position: 'top-right', autoClose: 4000, transition: Bounce });
        }
    };

    const fetchTenantData = React.useCallback(async () => {
        if (!activeTenantId) return;

        try {
            const tenants = await apiFetch(
                `${process.env.REACT_APP_API_URL}/tenants/my`,
                { headers: getAuthHeaders() },
                handleApiError
            );

            const currentTenant = tenants.find((t) => t._id === activeTenantId);

            if (currentTenant) {
                setTenant(currentTenant);
                setTenantName(currentTenant.name);
                setInviteCode(currentTenant.inviteCode || "");
            }

            const membersData = await apiFetch(
                `${process.env.REACT_APP_API_URL}/tenants/${activeTenantId}/members`,
                { headers: getAuthHeaders() },
                handleApiError
            );

            setMembers(membersData);
        } catch (error) {
            console.error("Error fetching tenant data:", error);
        } finally {
            setLoading(false);
        }
    }, [activeTenantId, getAuthHeaders, handleApiError]);

    // Fonction pour gérer la facturation via le Customer Portal de Stripe
    const handleManageBilling = async () => {
        if (!activeTenantId) return;
        
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/stripe/create-portal-session`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    },
                    body: JSON.stringify({ tenantId: activeTenantId })
                },
                handleApiError
            );
            
            if (response.portalUrl) {
                // Rediriger vers le Customer Portal de Stripe
                window.location.href = response.portalUrl;
            }
        } catch (error) {
            console.error('Erreur lors de l\'ouverture du portal de facturation:', error);
            
            // Si le customer Stripe n'existe pas, proposer de créer un abonnement
            if (error.code === 'STRIPE_CUSTOMER_NOT_FOUND') {
                toast.info('Aucun abonnement trouvé. Choisissez un plan pour commencer.', {
                    position: "top-right",
                    autoClose: 7000,
                });
                // Ouvrir le modal de pricing pour créer un abonnement
                setTimeout(() => setShowPricingModal(true), 1000);
            } else {
                toast.error('Impossible d\'accéder à la gestion de facturation', {
                    position: "top-right",
                    autoClose: 5000,
                });
            }
        }
    };

    // Helper to persist message settings immediately (tracks saving state)
    const saveMessageSettings = async (types, roles) => {
        if (!activeTenantId) return false;
        setSavingMessagesSettings(true);
        try {
            const payload = { messageTypes: types, messageRoles: roles };
            await apiFetch(
                `${process.env.REACT_APP_API_URL}/tenants/${activeTenantId}/settings/messages`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...getAuthHeaders()
                    },
                    body: JSON.stringify(payload)
                },
                handleApiError
            );
            try { window.dispatchEvent(new CustomEvent('messageSettingsChanged')); } catch (e) { /* ignore */ }
            return true;
        } catch (err) {
            console.error('Error saving message settings', err);
            return false;
        } finally {
            // small delay so the user can perceive the saving state
            setTimeout(() => setSavingMessagesSettings(false), 200);
        }
    };

    // Handlers for immediate add/remove with optimistic UI
    const handleAddType = async (typeName, color) => {
        if (!canEdit) return toast.error('Permissions insuffisantes');
        const v = (typeName || '').trim();
        if (!v) return;
        if (messageTypes.some(mt => mt.name === v)) {
            toast.info('Ce type existe déjà');
            return;
        }

        const prev = messageTypes;
        const updated = [...prev, { name: v, color }];
        setMessageTypes(updated);

        const ok = await saveMessageSettings(updated, messageRoles);
        if (ok) {
            toast.success('Type ajouté', { position: 'top-right', autoClose: 5000, transition: Bounce });
            setNewType('');
        } else {
            setMessageTypes(prev);
            toast.error('Erreur lors de l\'ajout du type', { position: 'top-right', autoClose: 5000, transition: Bounce });
        }
    };

    const handleRemoveType = async (idx) => {
        if (!canEdit) return toast.error('Permissions insuffisantes');
        const prev = messageTypes;
        const updated = prev.filter((_, i) => i !== idx);
        setMessageTypes(updated);

        const ok = await saveMessageSettings(updated, messageRoles);
        if (ok) {
            toast.success('Type supprimé', { position: 'top-right', autoClose: 5000, transition: Bounce });
        } else {
            setMessageTypes(prev);
            toast.error('Erreur lors de la suppression du type', { position: 'top-right', autoClose: 5000, transition: Bounce });
        }
    };

    const handleAddRole = async (roleName) => {
        if (!canEdit) return toast.error('Permissions insuffisantes');
        const v = (roleName || '').trim();
        if (!v) return;
        if (messageRoles.includes(v)) {
            toast.info('Ce rôle existe déjà');
            return;
        }

        const prev = messageRoles;
        const updated = [...prev, v];
        setMessageRoles(updated);

        const ok = await saveMessageSettings(messageTypes, updated);
        if (ok) {
            toast.success('Rôle ajouté', { position: 'top-right', autoClose: 5000, transition: Bounce });
            setNewRole('');
        } else {
            setMessageRoles(prev);
            toast.error('Erreur lors de l\'ajout du rôle', { position: 'top-right', autoClose: 5000, transition: Bounce });
        }
    };

    const handleRemoveRole = async (idx) => {
        if (!canEdit) return toast.error('Permissions insuffisantes');
        const prev = messageRoles;
        const updated = prev.filter((_, i) => i !== idx);
        setMessageRoles(updated);

        const ok = await saveMessageSettings(messageTypes, updated);
        if (ok) {
            toast.success('Rôle supprimé', { position: 'top-right', autoClose: 5000, transition: Bounce });
        } else {
            setMessageRoles(prev);
            toast.error('Erreur lors de la suppression du rôle', { position: 'top-right', autoClose: 5000, transition: Bounce });
        }
    };

    useEffect(() => {
        fetchTenantData();
    }, [fetchTenantData]);

    // Fetch message settings for the tenant
    const fetchMessageSettings = React.useCallback(async () => {
        if (!activeTenantId) return;
        try {
            const data = await apiFetch(
                `${process.env.REACT_APP_API_URL}/tenants/${activeTenantId}/settings/messages`,
                { headers: getAuthHeaders() },
                handleApiError
            );

            // Normalize messageTypes to objects { name, color }
            const DEFAULT_TYPE_COLORS = {
                'Rappel mensuel': '#3b82f6',
                'Rappel hebdomadaire': '#8b5cf6',
                'Rappel quotidien': '#06b6d4',
                'Notification': '#f59e0b',
                'Information': '#10b981',
                'Alerte': '#ef4444',
                'Mise à jour': '#6366f1'
            };

            const rawTypes = Array.isArray(data.messageTypes) ? data.messageTypes : [];
            const normalized = rawTypes.map(t => {
                if (!t) return null;
                if (typeof t === 'string') return { name: t, color: DEFAULT_TYPE_COLORS[t] || '#3b82f6' };
                return { name: t.name, color: t.color || DEFAULT_TYPE_COLORS[t.name] || '#3b82f6' };
            }).filter(Boolean);

            setMessageTypes(normalized);
            setMessageRoles(Array.isArray(data.messageRoles) ? data.messageRoles : []);
        } catch (err) {
            console.error('Error fetching message settings', err);
        }
    }, [activeTenantId, getAuthHeaders, handleApiError]);

    // Fetch branding settings (Enterprise)
    const fetchBranding = React.useCallback(async () => {
        if (!activeTenantId) return;
        try {
            const data = await apiFetch(
                `${process.env.REACT_APP_API_URL}/tenants/${activeTenantId}/settings/branding`,
                { headers: getAuthHeaders() },
                handleApiError
            );
            const b = data.branding || {};
            setBrandingName(b.name || '');
            setBrandingFont(b.font || 'Inter');
            setBrandingColor(b.color || '#111827');
            setBrandingSize(typeof b.size === 'number' ? Math.min(2, Math.max(0.9, b.size)) : 1.25);
        } catch (err) {
            console.error('Error fetching branding', err);
        }
    }, [activeTenantId, getAuthHeaders, handleApiError]);

    const handleSaveBranding = async (e) => {
        e.preventDefault();
        if (!canEdit) return toast.error('Permissions insuffisantes');
        setSavingBranding(true);
        try {
            await apiFetch(
                `${process.env.REACT_APP_API_URL}/tenants/${activeTenantId}/settings/branding`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                    body: JSON.stringify({ name: brandingName, font: brandingFont, color: brandingColor, size: brandingSize })
                },
                handleApiError
            );
            try { window.dispatchEvent(new CustomEvent('brandingUpdated')); } catch (_) { /* ignore */ }
            toast.success('Personnalisation enregistrée', { position: 'top-right', autoClose: 5000, transition: Bounce });
        } catch (err) {
            console.error('Error saving branding', err);
            toast.error('Erreur lors de l\'enregistrement', { position: 'top-right', autoClose: 5000, transition: Bounce });
        } finally {
            setSavingBranding(false);
        }
    };

    useEffect(() => {
        if (activeSection === 'messageSettings') {
            fetchMessageSettings();
        }
        if (activeSection === 'branding') {
            fetchBranding();
        }
        if (activeSection === 'audit') {
            fetchAuditLogs(1, '');
            setAuditAction('');
        }
    }, [activeSection, fetchMessageSettings, fetchBranding, fetchAuditLogs]);

    const handleUpdateTenant = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const updated = await apiFetch(
                `${process.env.REACT_APP_API_URL}/tenants/${activeTenantId}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        ...getAuthHeaders(),
                    },
                    body: JSON.stringify({ name: tenantName }),
                },
                handleApiError
            );

            setTenant(updated);
            toast.success("Organisation mise à jour", {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        } catch (error) {
            console.error("Error updating tenant:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleRegenerateInviteCode = async () => {
        try {
            const data = await apiFetch(
                `${process.env.REACT_APP_API_URL}/tenants/${activeTenantId}/regenerate-invite`,
                {
                    method: "POST",
                    headers: getAuthHeaders(),
                },
                handleApiError
            );

            setInviteCode(data.inviteCode);
            toast.success("Code d'invitation régénéré", {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        } catch (error) {
            console.error("Error regenerating invite code:", error);
        }
    };

    const handleCopyInviteCode = () => {
        navigator.clipboard.writeText(inviteCode);
        toast.success("Code copié dans le presse-papier", {
            position: "top-right",
            autoClose: 5000,
            theme: "light",
            transition: Bounce,
        });
    };

    const handleUpdateMemberRole = async (memberId, newRole) => {
        const member = members.find((m) => m._id === memberId);
        const currentUserRole = tenant?.userRole;

        if (newRole === "owner" && currentUserRole !== "owner") {
            toast.error("Seul le propriétaire peut nommer un autre propriétaire", {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
            return;
        }

        if (member.role === "owner" && newRole !== "owner") {
            const remainingOwners = members.filter(
                (m) => m.role === "owner" && m._id !== memberId
            );

            if (remainingOwners.length === 0) {
                toast.error(
                    "Impossible de retirer le statut owner : il doit toujours rester au moins un propriétaire dans l'organisation",
                    {
                        position: "top-right",
                        autoClose: 5000,
                        theme: "light",
                        transition: Bounce,
                    }
                );
                return;
            }
        }

        try {
            await apiFetch(
                `${process.env.REACT_APP_API_URL}/tenants/${activeTenantId}/members/${memberId}/role`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        ...getAuthHeaders(),
                    },
                    body: JSON.stringify({ role: newRole }),
                },
                handleApiError
            );

            fetchTenantData();
            toast.success("Rôle mis à jour", {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        } catch (error) {
            console.error("Error updating role:", error);
        }
    };

    const handleRemoveMember = async (memberId) => {
        const member = members.find((m) => m._id === memberId);

        // Empêcher l'auto-suppression
        if (memberId === user?._id || memberId === user?.id) {
            toast.error(
                "Vous ne pouvez pas vous retirer vous-même de l'organisation",
                {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                    transition: Bounce,
                }
            );
            return;
        }

        // Empêcher la suppression du dernier propriétaire
        if (member.role === "owner") {
            const remainingOwners = members.filter(
                (m) => m.role === "owner" && m._id !== memberId
            );
            if (remainingOwners.length === 0) {
                toast.error(
                    "Impossible de supprimer ce propriétaire : il doit rester au moins un propriétaire.",
                    {
                        position: "top-right",
                        autoClose: 5000,
                        theme: "light",
                        transition: Bounce,
                    }
                );
                return;
            }
        }

        // Vérifier les permissions selon le rôle (sécurisé avec hasTenantRole)
        const isAdmin = hasTenantRole(activeTenantId, ['admin']);
        const isOwnerRole = hasTenantRole(activeTenantId, ['owner']);
        
        if (isAdmin && !isOwnerRole && member.role !== "member") {
            toast.error("Les administrateurs ne peuvent supprimer que des membres", {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
            return;
        }

        setMemberToRemove(member);
        setShowRemoveModal(true);
    };

    const confirmRemoveMember = async () => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/tenants/${activeTenantId}/members/${memberToRemove._id}`,
                {
                    method: "DELETE",
                    headers: getAuthHeaders(),
                }
            );

            const data = await response.json();

            if (response.ok) {
                toast.success("Membre retiré avec succès", {
                    position: "top-right",
                    autoClose: 5000,
                    theme: "light",
                    transition: Bounce,
                });
                fetchTenantData();
            } else {
                handleApiError(response.status, data.message);
            }
        } catch (error) {
            console.error("Error removing member:", error);
            toast.error("Erreur lors de la suppression du membre", {
                position: "top-right",
                autoClose: 5000,
                theme: "light",
                transition: Bounce,
            });
        } finally {
            setShowRemoveModal(false);
            setMemberToRemove(null);
        }
    };

    const handleDeleteTenant = async () => {
        if (!isOwner) {
            toast.error("Seule le propriétaire peut supprimer l'organisation", {
                position: "top-right",
                autoClose: 5000,
            });
            return;
        }

        // Open the confirmation modal instead of using window.confirm
        setShowDeleteTenantModal(true);
    };

    const confirmDeleteTenant = async () => {
        setShowDeleteTenantModal(false);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/tenants/${activeTenantId}`,
                {
                    method: "DELETE",
                    headers: getAuthHeaders(),
                }
            );

            if (response.ok) {
                toast.success("Organisation supprimée", {
                    position: "top-right",
                    autoClose: 5000,
                });
                localStorage.removeItem("activeTenantId");
                navigate("/join-tenant");
            } else {
                try {
                    const data = await response.json();
                    toast.error(data.message || "Erreur lors de la suppression");
                } catch (parseErr) {
                    const text = await response.text().catch(() => null);
                    toast.error(
                        (text && String(text)) || "Erreur lors de la suppression"
                    );
                }
            }
        } catch (err) {
            console.error("Error deleting tenant:", err);
            toast.error("Erreur réseau");
        }
    };

    if (!activeTenantId) {
        return (
            <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
                <Header />
                <main className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md px-4">
                        <div className="text-6xl mb-4">⚙️</div>
                        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-3">
                            Aucune organisation
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            Pour accéder aux paramètres, créez une organisation ou rejoignez-en une existante.
                        </p>
                        <button
                            onClick={() => navigate('/join-tenant')}
                            className="bg-primary text-white dark:text-black font-semibold py-3 px-6 rounded hover:bg-primary-hover transition"
                        >
                            Créer ou rejoindre une organisation
                        </button>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    const getRoleLabel = (role) => {
        const labels = {
            owner: "Propriétaire",
            admin: "Administrateur",
            member: "Membre",
        };
        return labels[role] || role;
    };



    const userRole = tenant?.userRole; // Pour affichage uniquement
    // Utiliser hasTenantRole pour les vérifications de permissions (sécurisé avec normalizeRole)
    const canEdit = hasTenantRole(activeTenantId, ['owner', 'admin']);
    const isOwner = hasTenantRole(activeTenantId, ['owner']);

    const owners = members.filter((m) => m.role === "owner");
    const admins = members.filter((m) => m.role === "admin");

    // Quota enforcement: le plan Free est désormais limité à 1 utilisateur
    const isFreePlan = tenant?.plan === 'free';
    const planLimits = tenant?.planLimits || {};
    const resourceCounts = tenant?.resourceCounts || {};
    const overQuota = isFreePlan && (
        (planLimits.maxMembers && resourceCounts.members > 1) ||
        (planLimits.maxTodos && resourceCounts.todos > planLimits.maxTodos) ||
        (planLimits.maxMessages && resourceCounts.messages > planLimits.maxMessages)
    );

    const sections = [
        { id: "info", label: "Informations", icon: FiSettings, show: true },
        { id: "plan", label: "Plan", icon: FiUsers, show: isOwner },
        { id: "invite", label: "Invitations", icon: FiCopy, show: canEdit },
        { id: "members", label: "Membres", icon: FiUsers, show: true },
        { id: "messageSettings", label: "Paramètres des messages", icon: FiAlertCircle, show: canEdit && canUseMessageSettings },
        { id: "messageTemplates", label: "Modèles de messages", icon: FiFileText, show: canEdit && canUseTemplates },
        { id: "branding", label: "Personnalisation", icon: FiZap, show: canEdit && canUseCustomBranding },
        { id: "audit", label: "Audit", icon: FiActivity, show: canEdit }
    ].filter((s) => s.show);



    return (
        <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
            <Header />
            <main className="flex-1 pb-8">
                <div className="container mx-auto px-4 pt-4 sm:pt-8">
                    {/* Back Button */}
                    <button
                        onClick={() => navigate("/messages")}
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white mb-4 sm:mb-6"
                    >
                        <FiArrowLeft /> Retour aux messages
                    </button>

                    {/* Title */}
                    <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-black dark:text-white flex items-center gap-2">
                        <FiSettings className="hidden sm:inline" /> Gestion de
                        l'organisation
                    </h1>

                    {/* Mobile Section Selector */}
                    <div className="lg:hidden mb-4 bg-white dark:bg-gray-800 rounded-lg shadow p-2">
                        <select
                            value={activeSection}
                            onChange={(e) => handleTabChange(e.target.value)}
                            className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                        >
                            {sections.map((section) => (
                                <option key={section.id} value={section.id}>
                                    {section.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
                        {/* Desktop Sidebar */}
                        <div className="hidden lg:block lg:col-span-1">
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sticky top-24">
                                <nav className="space-y-1">
                                    {sections.map((section) => {
                                        const Icon = section.icon;
                                        return (
                                            <button
                                                key={section.id}
                                                onClick={() => handleTabChange(section.id)}
                                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${activeSection === section.id
                                                    ? "bg-primary text-white dark:text-black"
                                                    : "hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                                                    }`}
                                            >
                                                <Icon className="h-5 w-5" />
                                                <span className="font-medium text-start">{section.label}</span>
                                            </button>
                                        );
                                    })}
                                </nav>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="lg:col-span-3 space-y-4 sm:space-y-6">
                            {/* Informations générales */}
                            {activeSection === "info" && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex items-center gap-3">
                                            <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg backdrop-blur-sm">
                                                <FiSettings className="h-6 w-6 text-black dark:text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-black dark:text-white">
                                                    Informations de l'organisation
                                                </h2>
                                                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                                                    Gérez les informations de votre organisation
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <form onSubmit={handleUpdateTenant} className="space-y-6 p-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Nom de l'organisation
                                            </label>
                                            <input
                                                type="text"
                                                value={tenantName}
                                                onChange={(e) => setTenantName(e.target.value)}
                                                className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                                disabled={!canEdit || loading}
                                                required
                                                maxLength={40}
                                            />
                                            <div className="text-xs text-gray-500 pt-1">{tenantName.trim().length}/40</div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Type
                                                </label>
                                                <input
                                                    type="text"
                                                    value={
                                                        tenant?.type === "personal"
                                                            ? "Personnel"
                                                            : "Professionnel"
                                                    }
                                                    className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded"
                                                    disabled
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Plan
                                                </label>
                                                <input
                                                    type="text"
                                                    value={tenant?.plan}
                                                    className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded uppercase"
                                                    disabled
                                                />
                                            </div>
                                        </div>

                                        {canEdit && (
                                            <button
                                                    type="submit"
                                                    disabled={loading || tenantName.trim() === '' || tenantName.trim().length > 40}
                                                    className="w-full bg-primary text-white dark:text-black p-3 font-semibold rounded hover:bg-primary-hover transition"
                                                >
                                                    {loading ? "Mise à jour..." : "Mettre à jour"}
                                                </button>
                                        )}
                                    </form>
                                </div>
                            )}

                            {/* Section Plan */}
                            {activeSection === "plan" && isOwner && (
                                <div className="space-y-6">                                    
                                    {/* Statut du tenant */}
                                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                        <div className="p-6">
                                            <div className="flex items-center gap-3">
                                                <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg backdrop-blur-sm">
                                                    <FiUsers className="h-6 w-6 text-black dark:text-white" />
                                                </div>
                                                <div>
                                                    <h2 className="text-xl font-bold text-black dark:text-white">
                                                        Informations du compte
                                                    </h2>
                                                    <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                                                        Gérez vos informations personnelles et de compte
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="p-6">
                                            <TenantStatus 
                                                tenant={tenant} 
                                                onUpdatePayment={handleManageBilling}
                                                onReactivateSubscription={() => setShowPricingModal(true)}
                                                onChoosePlan={() => setShowPricingModal(true)}
                                            />
                                        </div>
                                        {/* Notification de statut si restrictions */}
                                        <div className="px-6 pb-6">
                                            <TenantStatusNotification tenant={tenant} />
                                        </div>
                                    </div>
                                    
                                    {/* Plan et usage */}
                                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                                            <div className="flex-1">
                                                <h2 className="text-lg font-semibold text-black dark:text-white mb-4">
                                                    Gestion de votre plan
                                                </h2>
                                                <PlanStatusBar 
                                                    tenant={tenant}
                                                    showUpgradeButton={true}
                                                    onUpgrade={() => setShowPricingModal(true)}
                                                />
                                                
                                                {/* Bouton pour gérer la facturation via Stripe */}
                                                {tenant?.status === 'active' && tenant?.plan !== 'free' && (
                                                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                                                        <button
                                                            onClick={handleManageBilling}
                                                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                                                        >
                                                            <FiSettings className="w-4 h-4 mr-2" />
                                                            Gérer la facturation
                                                        </button>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                                            Modifiez vos moyens de paiement, consultez vos factures et gérez votre abonnement
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Code d'invitation */}
                            {activeSection === "invite" && canEdit && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex items-center gap-3">
                                            <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg backdrop-blur-sm">
                                                <FiCopy className="h-6 w-6 text-black dark:text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-black dark:text-white">
                                                    Code d'invitation
                                                </h2>
                                                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                                                    Invitez de nouveaux membres à rejoindre votre organisation
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-6 space-y-6">
                                        {overQuota && (
                                            <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded flex items-center gap-2">
                                                <FiAlertCircle className="h-5 w-5 text-red-600 dark:text-red-500 flex-shrink-0" />
                                                <div className="text-sm text-red-800 dark:text-red-200">
                                                    <strong>Limite du plan gratuit dépassée.</strong> Supprimez des membres, todos ou messages pour pouvoir inviter de nouveaux membres.
                                                </div>
                                            </div>
                                        )}
                                    
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                                            Partagez ce code d'invitation avec les personnes que vous
                                            souhaitez inviter :
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <input
                                                type="text"
                                                value={inviteCode}
                                                readOnly
                                                className="flex-1 p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded font-mono text-sm sm:text-base"
                                            />
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={handleCopyInviteCode}
                                                    className="flex-1 sm:flex-none bg-gray-200 dark:bg-gray-600 text-black dark:text-white p-3 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition flex items-center justify-center gap-2"
                                                    title="Copier"
                                                >
                                                    <FiCopy className="h-5 w-5" />
                                                    <span className="sm:hidden">Copier</span>
                                                </button>
                                                <button
                                                    onClick={handleRegenerateInviteCode}
                                                    className={`flex-1 sm:flex-none bg-gray-200 dark:bg-gray-600 text-black dark:text-white p-3 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition flex items-center justify-center gap-2 ${overQuota ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    title="Régénérer"
                                                    disabled={overQuota}
                                                >
                                                    <FiRefreshCw className="h-5 w-5" />
                                                    <span className="sm:hidden">Régénérer</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Modèles de messages */}
                            {activeSection === "messageTemplates" && canEdit && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg backdrop-blur-sm">
                                                <FiFileText className="h-6 w-6 text-black dark:text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-black dark:text-white">
                                                    Modèles de messages
                                                </h2>
                                                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                                                    Accélérez la création de messages avec des modèles pré-définis
                                                </p>
                                            </div>
                                        </div>
                                        <MessageTemplates />
                                    </div>
                                </div>
                            )}

                            {/* Paramètres des messages */}
                            {activeSection === "messageSettings" && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex items-center gap-3">
                                            <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg backdrop-blur-sm">
                                                <FiAlertCircle className="h-6 w-6 text-black dark:text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-black dark:text-white">
                                                    Paramètres des messages
                                                </h2>
                                                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                                                    Gérez les types et rôles de messages
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-6 space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Types de message</label>
                                                <div className="flex flex-wrap gap-2 mb-3">
                                                    {messageTypes.map((t, idx) => (
                                                        <div key={t.name + idx} className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded px-3 py-1">
                                                            <span aria-hidden style={{ width: 12, height: 12, backgroundColor: t.color, borderRadius: 3, display: 'inline-block', flexShrink: 0 }} />
                                                            <span className="text-sm text-gray-800 dark:text-gray-200">{t.name}</span>
                                                            {canEdit && (
                                                                <button
                                                                    onClick={() => handleRemoveType(idx)}
                                                                    className="text-xs text-red-600 dark:text-red-400 ml-1"
                                                                    title="Supprimer"
                                                                >
                                                                    <FiX className="h-3 w-3" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="flex gap-2 items-center">
                                                    <input
                                                        type="text"
                                                        value={newType}
                                                        onChange={(e) => setNewType(e.target.value)}
                                                        className="flex-1 p-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded"
                                                        placeholder="Nouveau type de message"
                                                        disabled={!canEdit}
                                                    />
                                                    <input
                                                        type="color"
                                                        value={newTypeColor}
                                                        onChange={(e) => setNewTypeColor(e.target.value)}
                                                        title="Couleur du type"
                                                        className="w-12 h-[2.6rem] rounded border dark:border-gray-600"
                                                        disabled={!canEdit}
                                                    />
                                                    <button
                                                        onClick={() => handleAddType(newType, newTypeColor)}
                                                        className={`bg-primary text-white dark:text-black p-2 rounded ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        disabled={!canEdit}
                                                    >Ajouter</button>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rôles de message</label>
                                                <div className="flex flex-wrap gap-2 mb-3">
                                                    {messageRoles.map((r, idx) => (
                                                        <div key={r + idx} className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded px-3 py-1">
                                                            <span className="text-sm text-gray-800 dark:text-gray-200">{r}</span>
                                                            {canEdit && (
                                                                <button
                                                                    onClick={() => handleRemoveRole(idx)}
                                                                    className="text-xs text-red-600 dark:text-red-400 ml-1"
                                                                    title="Supprimer"
                                                                >
                                                                    <FiX className="h-3 w-3" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={newRole}
                                                        onChange={(e) => setNewRole(e.target.value)}
                                                        className="flex-1 p-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded"
                                                        placeholder="Nouveau rôle de message"
                                                        disabled={!canEdit}
                                                    />
                                                    <button
                                                        onClick={() => handleAddRole(newRole)}
                                                        className={`bg-primary text-white dark:text-black p-2 rounded ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        disabled={!canEdit}
                                                    >Ajouter</button>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-4 flex items-center gap-2">
                                            <span className="relative inline-flex w-5 h-5">
                                                {/* outer ring */}
                                                <span className={`absolute inset-0 rounded-full ${savingMessagesSettings ? 'bg-blue-100/60' : 'bg-transparent'}`} />
                                                {/* blue inner dot (pulse when saving) */}
                                                <span className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full w-2.5 h-2.5 ${savingMessagesSettings ? 'bg-blue-400 animate-pulse' : 'bg-blue-500'}`} />
                                                {/* subtle border to suggest a loader shape */}
                                                <span className="absolute inset-0 rounded-full border border-gray-200 dark:border-gray-700" />
                                            </span>
                                            <div className="text-sm text-gray-600 dark:text-gray-400">Modifications enregistrées automatiquement.</div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Personnalisation de la marque (Enterprise) */}
                            {activeSection === "branding" && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex items-center gap-3">
                                            <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg backdrop-blur-sm">
                                                <FiZap className="h-6 w-6 text-black dark:text-white" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-bold text-black dark:text-white flex items-center gap-2">
                                                    Personnalisation de la marque
                                                    <span className="text-xs font-semibold bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full">Enterprise</span>
                                                </h2>
                                                <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                                                    Remplacez le logo PulseCIO par le nom de votre marque dans le header
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <form onSubmit={handleSaveBranding} className="p-6 space-y-6">
                                        {/* Aperçu en temps réel */}
                                        <div className="rounded-lg border dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
                                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3 tracking-wide">Aperçu dans le header</p>
                                            <div className="flex items-center gap-4 bg-white dark:bg-gray-900 rounded-lg px-5 py-3 shadow-sm border dark:border-gray-700">
                                                {brandingName.trim() ? (
                                                    <span
                                                        style={{
                                                            fontFamily: brandingFont || 'inherit',
                                                            color: brandingColor || '#111827',
                                                            fontSize: `${brandingSize}rem`,
                                                            fontWeight: 700,
                                                            letterSpacing: '-0.01em'
                                                        }}
                                                    >
                                                        {brandingName}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 dark:text-gray-600 italic text-sm">Entrez un nom pour voir l'aperçu…</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Nom de marque */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Nom de la marque <span className="text-gray-400">(affiché à la place du logo)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={brandingName}
                                                onChange={(e) => setBrandingName(e.target.value)}
                                                className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                                placeholder="Ex: Acme Corp, Direction IT…"
                                                maxLength={60}
                                                disabled={!canEdit}
                                            />
                                            <div className="text-xs text-gray-400 pt-1">{brandingName.length}/60 — laissez vide pour afficher le logo PulseCIO</div>
                                        </div>

                                        {/* Police */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Police de caractères
                                            </label>
                                            <select
                                                value={brandingFont}
                                                onChange={(e) => {
                                                    setBrandingFont(e.target.value);
                                                    // Charger la police pour l'aperçu
                                                    const font = e.target.value;
                                                    if (font && font !== 'Georgia' && font !== 'Courier New') {
                                                        const encoded = encodeURIComponent(font);
                                                        if (!document.querySelector(`link[href*="${encoded}"]`)) {
                                                            const link = document.createElement('link');
                                                            link.rel = 'stylesheet';
                                                            link.href = `https://fonts.googleapis.com/css2?family=${encoded}:wght@400;600;700&display=swap`;
                                                            document.head.appendChild(link);
                                                        }
                                                    }
                                                }}
                                                className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                                disabled={!canEdit}
                                            >
                                                {BRAND_FONTS.map((f) => (
                                                    <option key={f.value} value={f.value}>{f.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Couleur */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Couleur du texte
                                            </label>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="color"
                                                    value={brandingColor}
                                                    onChange={(e) => setBrandingColor(e.target.value)}
                                                    className="h-10 w-16 rounded border dark:border-gray-600 cursor-pointer"
                                                    disabled={!canEdit}
                                                />
                                                <input
                                                    type="text"
                                                    value={brandingColor}
                                                    onChange={(e) => setBrandingColor(e.target.value)}
                                                    className="w-32 p-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary font-mono text-sm"
                                                    placeholder="#111827"
                                                    maxLength={20}
                                                    disabled={!canEdit}
                                                />
                                                <div className="flex gap-2">
                                                    {['#111827', '#1d4ed8', '#7c3aed', '#b45309', '#047857', '#dc2626'].map((c) => (
                                                        <button
                                                            key={c}
                                                            type="button"
                                                            onClick={() => setBrandingColor(c)}
                                                            style={{ backgroundColor: c }}
                                                            className="w-7 h-7 rounded-full border-2 border-white dark:border-gray-700 shadow hover:scale-110 transition"
                                                            title={c}
                                                            disabled={!canEdit}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Taille */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Taille du texte
                                                <span className="ml-2 text-xs text-gray-400 font-normal">
                                                    {brandingSize.toFixed(2)} rem
                                                    {brandingSize >= 2 && <span className="ml-1 text-orange-500">(maximum)</span>}
                                                </span>
                                            </label>
                                            <div className="flex items-center gap-3">
                                                <span className="text-xs text-gray-400 w-10">Petit</span>
                                                <input
                                                    type="range"
                                                    min="0.9"
                                                    max="2"
                                                    step="0.05"
                                                    value={brandingSize}
                                                    onChange={(e) => setBrandingSize(parseFloat(e.target.value))}
                                                    className="flex-1 accent-primary"
                                                    disabled={!canEdit}
                                                />
                                                <span className="text-xs text-gray-400 w-12 text-right">Grand</span>
                                            </div>
                                            <p className="text-xs text-gray-400 mt-1">Limité à 2 rem pour éviter d'agrandir le header</p>
                                        </div>

                                        {canEdit && (
                                            <div className="flex justify-end">
                                                <button
                                                    type="submit"
                                                    disabled={savingBranding}
                                                    className="bg-primary text-white dark:text-black px-6 py-2.5 rounded-lg font-semibold hover:bg-primary-hover transition disabled:opacity-60"
                                                >
                                                    {savingBranding ? 'Enregistrement…' : 'Enregistrer la personnalisation'}
                                                </button>
                                            </div>
                                        )}
                                    </form>
                                </div>
                            )}

                            {/* Liste des membres */}
                            {activeSection === "members" && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-6">
                                        <div className="flex justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg backdrop-blur-sm">
                                                    <FiUsers className="h-6 w-6 text-black dark:text-white" />
                                                </div>
                                                <div>
                                                    <h2 className="text-xl font-bold text-black dark:text-white">
                                                        Membres ({members.length})
                                                    </h2>
                                                    <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
                                                        Gérez les membres de votre organisation
                                                    </p>
                                                </div> 
                                            </div>
                                            <div className="text-sm text-gray-600 dark:text-gray-400">
                                                {owners.length} Propriétaire{owners.length > 1 ? "s" : ""}{" "}
                                                • {admins.length} Administrateur
                                                {admins.length > 1 ? "s" : ""}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2 p-6">
                                        {members.map((member) => {
                                            // Vérifier si c'est l'utilisateur actuel
                                            const isCurrentUser =
                                                member._id === user?._id || member._id === user?.id;

                                            // Permissions pour modifier le rôle (sécurisé avec hasTenantRole)
                                            const isAdminUser = hasTenantRole(activeTenantId, ['admin']);
                                            const isOwnerUser = hasTenantRole(activeTenantId, ['owner']);
                                            let canChangeRole = false;

                                            if (!isCurrentUser) {
                                                // Owner peut tout modifier
                                                if (isOwnerUser) {
                                                    canChangeRole = true;
                                                }
                                                // Admin peut modifier les membres et les autres admins
                                                else if (
                                                    isAdminUser &&
                                                    ["member", "admin"].includes(member.role)
                                                ) {
                                                    canChangeRole = true;
                                                }
                                            } else {
                                                // L'utilisateur peut modifier son propre rôle uniquement si :
                                                // - Il est Owner ET qu'il reste au moins un autre Owner après modification
                                                // - Il est Admin (peut se rétrograder en membre)
                                                if (isOwnerUser) {
                                                    const otherOwners = owners.filter(
                                                        (o) => o._id !== member._id
                                                    );
                                                    canChangeRole = otherOwners.length > 0;
                                                } else if (isAdminUser) {
                                                    canChangeRole = true;
                                                }
                                            }

                                            // Permissions pour retirer un membre (ne pas se retirer soi-même) (sécurisé avec hasTenantRole)
                                            let canRemove = false;

                                            if (!isCurrentUser) {
                                                // Owner peut retirer tout le monde
                                                if (isOwnerUser) {
                                                    canRemove = true;
                                                }
                                                // Admin peut retirer uniquement les membres
                                                else if (
                                                    isAdminUser &&
                                                    member.role === "member"
                                                ) {
                                                    canRemove = true;
                                                }
                                            }

                                            return (
                                                <div
                                                    key={member._id}
                                                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border dark:border-gray-700 rounded-lg gap-3"
                                                >
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1 justify-between md:justify-normal">
                                                            <p className="font-semibold text-black dark:text-white truncate">
                                                                {member.name}
                                                                {isCurrentUser && (
                                                                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                                                        (Vous)
                                                                    </span>
                                                                )}
                                                            </p>
                                                            <span
                                                                className={`text-xs px-2 py-1 rounded whitespace-nowrap ${member.role === "owner"
                                                                    ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
                                                                    : member.role === "admin"
                                                                        ? "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                                                                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                                                    }`}
                                                            >
                                                                {getRoleLabel(member.role)}
                                                            </span>
                                                        </div>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                                            {member.email}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                                            Membre depuis le{" "}
                                                            {new Date(member.joinedAt).toLocaleDateString(
                                                                "fr-FR"
                                                            )}
                                                        </p>
                                                    </div>

                                                    <div className="flex justify-end gap-2 sm:flex-shrink-0">
                                                        {canChangeRole ? (
                                                            <select
                                                                value={member.role}
                                                                onChange={(e) =>
                                                                    handleUpdateMemberRole(
                                                                        member._id,
                                                                        e.target.value
                                                                    )
                                                                }
                                                                className="flex-1 px-3 py-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded text-sm"
                                                            >
                                                                <option value="member">Membre</option>
                                                                <option value="admin">Administrateur</option>
                                                                {isOwnerUser && (
                                                                    <option value="owner">Propriétaire</option>
                                                                )}
                                                            </select>
                                                        ) : (
                                                            <select
                                                                value={member.role}
                                                                disabled
                                                                title={
                                                                    isCurrentUser
                                                                        ? "Impossible de modifier votre rôle si vous êtes le seul propriétaire"
                                                                        : isAdminUser
                                                                            ? "Les administrateurs ne peuvent modifier que des membres et des administrateurs"
                                                                            : "Vous n'avez pas la permission de modifier ce rôle"
                                                                }
                                                                className="flex-1 px-3 py-2 border dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 rounded text-sm opacity-70 cursor-not-allowed"
                                                            >
                                                                <option value={member.role}>
                                                                    {getRoleLabel(member.role)}
                                                                </option>
                                                            </select>
                                                        )}

                                                        {canRemove ? (
                                                            <button
                                                                onClick={() => handleRemoveMember(member._id)}
                                                                className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition text-sm whitespace-nowrap"
                                                                title="Retirer ce membre"
                                                            >
                                                                Retirer
                                                            </button>
                                                        ) : (
                                                            <button
                                                                disabled
                                                                className="px-3 py-2 bg-red-400 text-white rounded text-sm whitespace-nowrap opacity-70 cursor-not-allowed"
                                                                title={
                                                                    isCurrentUser
                                                                        ? "Vous ne pouvez pas vous retirer vous-même"
                                                                        : member.role === "owner"
                                                                            ? "Impossible de supprimer le dernier propriétaire"
                                                                            : "Vous n'avez pas la permission de supprimer ce membre"
                                                                }
                                                            >
                                                                Retirer
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    
                                    {owners.length === 1 && admins.length === 0 && (
                                        <div className="space-y-6 p-6">
                                            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded flex items-start gap-2">
                                                <FiAlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                                                <div className="text-sm text-yellow-800 dark:text-yellow-200">
                                                    <p className="font-semibold mb-1">
                                                        Recommandation de sécurité
                                                    </p>
                                                    <p>
                                                        Il est conseillé de nommer au moins un administrateur
                                                        pour éviter de perdre l'accès à l'organisation.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Section Audit */}
                            {activeSection === "audit" && canEdit && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-6 border-b dark:border-gray-700">
                                        <div className="flex items-center justify-between gap-4 flex-wrap">
                                            <div className="flex items-center gap-3">
                                                <div className="p-3 bg-gray-100 dark:bg-white/20 rounded-lg">
                                                    <FiActivity className="h-6 w-6 text-black dark:text-white" />
                                                </div>
                                                <div>
                                                    <h2 className="text-xl font-bold text-black dark:text-white">Journal d'audit</h2>
                                                    <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">{auditTotal} événement(s) enregistré(s)</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <div className="flex items-center gap-2 text-sm">
                                                    <FiFilter className="text-gray-500 dark:text-gray-400 h-4 w-4" />
                                                    <select
                                                        value={auditAction}
                                                        onChange={(e) => { setAuditAction(e.target.value); fetchAuditLogs(1, e.target.value); }}
                                                        className="p-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded text-sm focus:ring-2 focus:ring-primary"
                                                    >
                                                        <option value="">Toutes les actions</option>
                                                        <option value="login">Connexion</option>
                                                        <option value="logout">Déconnexion</option>
                                                        <option value="login_failed">Échec connexion</option>
                                                        <option value="account_locked">Compte verrouillé</option>
                                                        <option value="member_role_changed">Rôle modifié</option>
                                                        <option value="member_joined">Membre rejoint</option>
                                                        <option value="member_removed">Membre supprimé</option>
                                                        <option value="member_left">Membre parti</option>
                                                        <option value="tenant_created">Organisation créée</option>
                                                        <option value="tenant_updated">Organisation modifiée</option>
                                                        <option value="tenant_deleted">Organisation supprimée</option>
                                                        <option value="tenant_plan_changed">Plan modifié</option>
                                                        <option value="message_created">Message créé</option>
                                                        <option value="message_updated">Message modifié</option>
                                                        <option value="message_deleted">Message supprimé</option>
                                                        <option value="messages_exported">Messages exportés</option>
                                                        <option value="personal_data_exported">Données RGPD exportées</option>
                                                        <option value="password_changed">Mot de passe modifié</option>
                                                        <option value="profile_updated">Profil modifié</option>
                                                        <option value="account_deleted">Compte supprimé</option>
                                                        <option value="todo_created">Tâche créée</option>
                                                        <option value="todo_updated">Tâche modifiée</option>
                                                        <option value="todo_deleted">Tâche supprimée</option>
                                                        <option value="todos_imported">Tâches importées</option>
                                                    </select>
                                                </div>
                                                {isOwner && (
                                                    <button
                                                        onClick={handleExportAudit}
                                                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-black dark:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                                                    >
                                                        <FiDownload className="h-4 w-4" />
                                                        Export CSV
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        {auditLoading ? (
                                            <div className="p-8 text-center text-gray-500 dark:text-gray-400">Chargement...</div>
                                        ) : auditLogs.length === 0 ? (
                                            <div className="p-8 text-center text-gray-500 dark:text-gray-400">Aucun événement trouvé.</div>
                                        ) : (
                                            <table className="w-full text-sm">
                                                <thead className="bg-gray-50 dark:bg-gray-700 text-left">
                                                    <tr>
                                                        <th className="px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Date</th>
                                                        <th className="px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Acteur</th>
                                                        <th className="px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Action</th>
                                                        <th className="px-4 py-3 font-medium text-gray-600 dark:text-gray-300">IP</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y dark:divide-gray-700">
                                                    {auditLogs.map((log) => (
                                                        <tr key={log._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                                            <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                                                {new Date(log.createdAt).toLocaleString('fr-FR')}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="font-medium text-black dark:text-white">{log.actorName || '—'}</div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400">{log.actorEmail || ''}</div>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                                                    {log.action}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">{log.ip || '—'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>

                                    {auditPages > 1 && (
                                        <div className="p-4 border-t dark:border-gray-700 flex items-center justify-between text-sm">
                                            <span className="text-gray-500 dark:text-gray-400">Page {auditPage} / {auditPages}</span>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => fetchAuditLogs(auditPage - 1, auditAction)}
                                                    disabled={auditPage <= 1}
                                                    className="px-3 py-1.5 rounded border dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition text-black dark:text-white"
                                                >
                                                    Précédent
                                                </button>
                                                <button
                                                    onClick={() => fetchAuditLogs(auditPage + 1, auditAction)}
                                                    disabled={auditPage >= auditPages}
                                                    className="px-3 py-1.5 rounded border dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition text-black dark:text-white"
                                                >
                                                    Suivant
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Danger zone: delete organization (owner only) - visible only in Informations section */}
                            {isOwner && activeSection === "info" && (
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6">
                                    <h2 className="text-lg font-semibold mb-2 text-black dark:text-white">
                                        Zone de danger : Suppression de l'organisation
                                    </h2>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                        Supprimer définitivement cette organisation. Cette action
                                        supprimera toutes les données liées à cette organisation.
                                    </p>
                                    <div>
                                        <button
                                            onClick={handleDeleteTenant}
                                            className="w-full sm:w-auto bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition font-semibold"
                                        >
                                            Supprimer l'organisation
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <RemoveMemberModal
                open={showRemoveModal}
                onClose={() => {
                    setShowRemoveModal(false);
                    setMemberToRemove(null);
                }}
                member={memberToRemove}
                onConfirm={confirmRemoveMember}
                currentUserRole={userRole}
            />

            <DeleteModal
                open={showDeleteTenantModal}
                onClose={() => setShowDeleteTenantModal(false)}
                onConfirm={confirmDeleteTenant}
                subject="organisation"
                message={{
                    title: tenant?.name || "Organisation",
                    type: tenant?.type || "",
                    role: tenant?.plan || "",
                    content:
                        "Cette action supprimera définitivement toutes les données de l'organisation.",
                }}
            />

            <PricingModal
                open={showPricingModal}
                onClose={() => setShowPricingModal(false)}
                currentPlan={tenant?.plan}
                tenantId={activeTenantId}
                onSuccess={fetchTenantData}
            />



            <Footer />
        </div>
    );
};

export default TenantSettings;
